import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import SettingsCard from '../../components/SettingsCard';
import useOrgs from '@/src/Orgs/components/hooks/useOrgs';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';

/*
 * Org stats — the year-scale question ("how much did we do?") that
 * Home (today) and per-event Insights don't answer. Backed by the
 * org-scoped GET /orgs/:orgId/stats added for this page; media and
 * share counts bucket by the ACTIVATION's date, matching how the
 * team thinks about a year's work.
 */

interface YearCount {
    year: number;
    count: number;
}

export default function OrgStatsV2() {
    const { orgId } = useUserOrg();
    const { getOrgStats } = useOrgs();

    const [stats, setStats] = useState<{
        activationsByYear: YearCount[];
        mediaByYear: YearCount[];
        sharesByYear: YearCount[];
    } | null>(null);
    const [failed, setFailed] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        setIsLoading(true);
        setFailed(false);
        getOrgStats(orgId)
            .then((data) => {
                if (mounted) setStats(data);
            })
            .catch(() => {
                if (mounted) setFailed(true);
            })
            .finally(() => mounted && setIsLoading(false));
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    const thisYear = moment().year();
    const countFor = (rows: YearCount[] | undefined, year: number) =>
        rows?.find((row) => row.year === year)?.count ?? 0;

    const years = useMemo(() => {
        if (!stats) return [];
        const all = new Set<number>([
            ...stats.activationsByYear.map((r) => r.year),
            ...stats.mediaByYear.map((r) => r.year),
            ...stats.sharesByYear.map((r) => r.year),
        ]);
        return Array.from(all)
            .filter((year) => Number.isFinite(year))
            .sort((a, b) => b - a);
    }, [stats]);

    if (isLoading) {
        return (
            <div className="mb-6 h-40 animate-pulse rounded-[16px] bg-secondary" />
        );
    }

    if (failed || !stats) {
        return (
            <SettingsCard
                title="Stats"
                desc="Couldn't load the org rollups — refresh to retry."
            />
        );
    }

    return (
        <>
            <SettingsCard
                title="This year"
                desc="Org-wide totals. Photos and shares count toward the year their event ran."
            >
                <div className="flex flex-wrap gap-9">
                    {[
                        {
                            label: 'Activations',
                            value: countFor(
                                stats.activationsByYear,
                                thisYear,
                            ),
                        },
                        {
                            label: 'Photos',
                            value: countFor(stats.mediaByYear, thisYear),
                        },
                        {
                            label: 'Shares',
                            value: countFor(stats.sharesByYear, thisYear),
                        },
                    ].map((tile) => (
                        <div key={tile.label}>
                            <div className="text-[24px] font-bold leading-none tracking-[-0.01em] tabular-nums">
                                {tile.value.toLocaleString()}
                            </div>
                            <div className="mt-1.5 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-faint">
                                {tile.label}
                            </div>
                        </div>
                    ))}
                </div>
            </SettingsCard>

            <SettingsCard title="By year">
                {years.length === 0 ? (
                    <p className="text-[13px] text-muted-foreground">
                        Nothing yet — numbers appear as events run.
                    </p>
                ) : (
                    <div className="max-w-[560px]">
                        <div className="flex items-center gap-3 border-b border-line py-2 font-mono text-[9px] font-semibold uppercase tracking-[0.12em] text-faint">
                            <span className="w-[52px] flex-none">Year</span>
                            <span className="ml-auto w-[100px] text-right">
                                Activations
                            </span>
                            <span className="w-[110px] text-right">
                                Photos
                            </span>
                            <span className="w-[110px] text-right">
                                Shares
                            </span>
                        </div>
                        {years.map((year) => {
                            const current = year === thisYear;
                            return (
                                <div
                                    key={year}
                                    className="flex items-center gap-3 border-b border-line py-2 text-[12.5px] last:border-b-0"
                                >
                                    <span className="w-[52px] flex-none font-mono text-[11px] font-semibold">
                                        {year}
                                    </span>
                                    {[
                                        countFor(
                                            stats.activationsByYear,
                                            year,
                                        ),
                                        countFor(stats.mediaByYear, year),
                                        countFor(stats.sharesByYear, year),
                                    ].map((value, i) => (
                                        <span
                                            key={i}
                                            className={
                                                'text-right tabular-nums ' +
                                                (i === 0
                                                    ? 'ml-auto w-[100px]'
                                                    : 'w-[110px]') +
                                                (current
                                                    ? ' font-semibold'
                                                    : ' text-muted-foreground')
                                            }
                                        >
                                            {value.toLocaleString()}
                                        </span>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                )}
            </SettingsCard>
        </>
    );
}
